import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ErrorBoundary } from './components/ui';
import './lcars-theme.css';
import NavBar from './components/NavBar';
import HomePage from './components/HomePage';
import LoginPage from './components/LoginPage';
import SignupPage from './components/SignupPage';
import MFASetupPage from './components/MFASetupPage';
import MFAVerification from './components/MFAVerification';
import ResetPasswordRequest from './components/ResetPasswordRequest';
import ResetPasswordVerify from './components/ResetPasswordVerify';
import ProtectedRoute from './components/ProtectedRoute';
import AdminPanel from './components/AdminPanel';
import PortalSettings from './components/PortalSettings';
import ApplicationManager from './components/ApplicationManager';
import ApplicationDetails from './components/ApplicationDetails';
import CompanyManager from './components/CompanyManager';
import MicrosoftOAuthConfig from './components/MicrosoftOAuthConfig';
import OAuthCallback from './components/OAuthCallback';
import VerifyEmailPage from './components/VerifyEmailPage';
import CategoryManagement from './components/CategoryManagement';
import RoleManager from './components/RoleManager';
import CertificatesPage from './components/CertificatesPage';
import SecurityPage from './components/SecurityPage';
import SystemLogsPage from './components/SystemLogsPage';
import ApiKeysPage from './components/ApiKeysPage';
import TeamAdmin from './components/TeamAdmin';
import ImpersonationBanner from './components/ImpersonationBanner';
import Tasks from './components/TasksMenu';

import OnboardWizard from './components/OnboardWizard';
import OffboardWizard from './components/OffboardWizard';

import Management from './components/Management';
import SettingsPage from './components/SettingsPage';
import PendingApproval from './components/PendingApproval';



function App() {
  return (
    <ErrorBoundary>
    <ThemeProvider>
    <Router>
      <Routes>
        {/* Redirect bridge — OUTSIDE AuthProvider per MS best practice.
            This page makes no auth API calls and performs a full page
            navigation after setting cookies, so the main app mounts fresh. */}
        <Route path="/oauth/callback" element={<OAuthCallback />} />
        {/* Everything else — wrapped in AuthProvider */}
        <Route path="/*" element={<AuthedApp />} />
      </Routes>
    </Router>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

function AuthedApp() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

function AppContent() {
  const location = useLocation();
  const { user } = useAuth();
  const isImpersonating = !!(user?.impersonating);
  const isAuthenticated = !!user;
  const authPages = ['/login', '/signup', '/reset-password', '/reset-password-verify', '/mfa-verify', '/verify-email', '/oauth/callback', '/pending'];
  const showNav = isAuthenticated && !authPages.includes(location.pathname);
  const topPadding = !showNav ? '0' : isImpersonating ? '104px' : '64px';
  return (
      <div className="App" style={{ paddingTop: topPadding }}>
        <ImpersonationBanner />
        <NavBar />
        <Routes>
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/mfa-setup" element={<MFASetupPage />} />
          <Route path="/mfa-verify" element={<MFAVerification />} />
          <Route path="/reset-password" element={<ResetPasswordRequest />} />
          <Route path="/reset-password-verify" element={<ResetPasswordVerify />} />
          <Route path="/pending" element={<PendingApproval />} />
          <Route path="/admin" element={<ProtectedRoute requiredPermission="global_admin"><AdminPanel /></ProtectedRoute>} />
          <Route path="/profile" element={<Navigate to="/settings" replace />} />
          <Route path="/portals/:portalId/settings" element={<ProtectedRoute><PortalSettings /></ProtectedRoute>} />
          <Route path="/applications" element={<ProtectedRoute requiredPermission="global_admin"><ApplicationManager /></ProtectedRoute>} />
          <Route path="/applications/:appId" element={<ProtectedRoute requiredPermission="global_admin"><ApplicationDetails /></ProtectedRoute>} />
          <Route path="/companies" element={<ProtectedRoute requiredPermission="global_admin"><CompanyManager /></ProtectedRoute>} />
          <Route path="/microsoft-oauth" element={<ProtectedRoute requiredPermission="global_admin"><MicrosoftOAuthConfig /></ProtectedRoute>} />
          <Route path="/categories" element={<ProtectedRoute requiredPermission="global_admin"><CategoryManagement /></ProtectedRoute>} />
          <Route path="/roles" element={<ProtectedRoute requiredPermission="global_admin"><RoleManager /></ProtectedRoute>} />
          <Route path="/certificates" element={<ProtectedRoute requiredPermission="global_admin"><CertificatesPage /></ProtectedRoute>} />
          <Route path="/security" element={<ProtectedRoute requiredPermission="global_admin"><SecurityPage /></ProtectedRoute>} />
          <Route path="/system-logs" element={<ProtectedRoute requiredPermission="global_admin"><SystemLogsPage /></ProtectedRoute>} />
          <Route path="/api-keys" element={<ProtectedRoute requiredPermission="global_admin"><ApiKeysPage /></ProtectedRoute>} />
          <Route path="/team" element={<ProtectedRoute requiredPermission="company_admin"><TeamAdmin /></ProtectedRoute>} />
          <Route path="/tasks" element={<ProtectedRoute requiredPermission="company_admin"><Tasks /></ProtectedRoute>} />
          <Route path="/tasks/onboard-user" element={<ProtectedRoute requiredPermission="company_admin"><OnboardWizard /></ProtectedRoute>} />
          <Route path="/tasks/user-details" element={<Navigate to="/admin" replace />} />
          <Route path="/tasks/offboard-user" element={<ProtectedRoute requiredPermission="company_admin"><OffboardWizard /></ProtectedRoute>} />
          <Route path="/tasks/create-app" element={<ProtectedRoute requiredPermission="global_admin"><Navigate to="/applications" state={{ from: '/tasks' }} replace /></ProtectedRoute>} />
          <Route path="/tasks/security-review" element={<ProtectedRoute requiredPermission="global_admin"><Navigate to="/security" state={{ from: '/tasks' }} replace /></ProtectedRoute>} />
          <Route path="/management" element={<ProtectedRoute requiredPermission="global_admin"><Management /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />


        </Routes>
      </div>
  );
}

export default App;